import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { PlainButton } from 'components/v3';
import { formatAnswer } from '../../shared/answerFormat';
import { formatDateTime } from '../../shared/dates';
import Glyph from '../Glyph';
import { ORANGE, sx } from '../sx';
import DownloadMenu, { BarButton } from './DownloadMenu';
import EntryEditor from './EntryEditor';
import {
  changedKeys,
  columnsForWidth,
  contactOf,
  heroOf,
  iconFor,
  patchOf,
  sectionsOf,
  spans,
} from './entryModel';
import EntryValue from './EntryValue';

// The editorial entry card (design entry_detail.jsx, Implementation List
// §1): a bar with the entry ID, when it came in and the actions, a hero
// with the title and contact chips, then each section as a grid of icon
// tiles. Click any tile (or Edit) to edit; Save sends only what changed.
// Sizes follow the list exactly: title 26/800, values 15/600, labels
// 10.5/700 caps, 18px icon tiles, 4/3/2/1 columns by the card's width.
//
// Optional W-9 slots (w9-plan T15, §6.3 item 9), every one off by default
// so a regular form's card is unchanged:
//   extraActions      node in the action bar, before Download
//   extraSections     node after the answer sections
//   editableFieldIds  only these ids open an editor; the rest stay read-
//                     only tiles (null = every field, as before)
//   downloadItems     extra rows for the Download menu
//   onReveal(fieldId), revealed { [fieldId]: text }, revealingId
//                     the TIN reveal (EntryValue)

const Shell = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  color: ${(p) => sx(p.theme).text};
`;

const Bar = styled.div`
  position: relative;
  z-index: 2;
  flex: none;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(p) => (p.$mobile ? '10px' : '10px 16px')};
  margin: ${(p) => (p.$mobile ? '0 16px' : '0 28px')};
  padding: ${(p) => (p.$mobile ? '12px 0' : '14px 0')};
  border-bottom: 1px solid ${(p) => sx(p.theme).line};
`;

const Meta = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 18px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const MetaKey = styled.span`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  color: ${(p) => sx(p.theme).label};
`;

const MetaBox = styled.span`
  display: inline-flex;
  align-items: center;
  height: ${(p) => (p.$mobile ? '30px' : '32px')};
  padding: 0 ${(p) => (p.$mobile ? '9px' : '11px')};
  border-radius: ${(p) => (p.$mobile ? '8px' : '9px')};
  border: 1px solid ${(p) => sx(p.theme).line2};
  background: ${(p) => sx(p.theme).inset};
  font-size: ${(p) => (p.$mobile ? '12.5px' : '13px')};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const Edited = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  background: ${(p) => sx(p.theme).tint};
  border: 1px solid ${(p) => sx(p.theme).tintB};
  color: ${(p) => sx(p.theme).otext};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: ${(p) => (p.$mobile ? '0' : 'auto')};
  ${(p) => (p.$mobile ? 'order: -1; flex: 1 1 100%; flex-wrap: wrap;' : '')}
`;

const Nav = styled.div`
  display: inline-flex;
  align-items: center;
  height: 36px;
  border: 1px solid ${(p) => sx(p.theme).line2};
  border-radius: 10px;
  background: ${(p) => sx(p.theme).inset};
  overflow: hidden;
  ${(p) => (p.$mobile ? 'margin-right: auto;' : '')}
`;

const NavButton = styled(PlainButton)`
  width: ${(p) => (p.$mobile ? '40px' : '34px')};
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => sx(p.theme).text2};

  &:hover:not(:disabled) {
    color: ${(p) => sx(p.theme).otext};
    background: ${(p) => sx(p.theme).tint};
  }

  &:disabled {
    opacity: 0.3;
  }
`;

const NavText = styled.span`
  padding: 0 4px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: ${(p) => sx(p.theme).label};
`;

const EditButton = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1.5px solid rgba(250, 70, 22, 0.6);
  color: ${(p) => sx(p.theme).otext};
  font-size: 14px;
  font-weight: 700;
  transition: background 120ms ease, border-color 120ms ease;

  &:hover {
    background: ${(p) => sx(p.theme).tint};
    border-color: ${ORANGE};
  }
`;

const TextButton = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: ${(p) => (p.$mobile ? '40px' : '36px')};
  padding: 0 14px;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 700;
  white-space: nowrap;
  ${(p) => (p.$mobile ? 'flex: 1;' : '')}
  border: 1px solid ${(p) => (p.$primary ? ORANGE : sx(p.theme).line2)};
  background: ${(p) => (p.$primary ? ORANGE : 'transparent')};
  color: ${(p) => (p.$primary ? '#FFFFFF' : sx(p.theme).text2)};

  &:hover:not(:disabled) {
    border-color: ${ORANGE};
    color: ${(p) => (p.$primary ? '#FFFFFF' : sx(p.theme).text)};
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const Dirty = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  color: ${(p) => sx(p.theme).otext};
`;

const CloseButton = styled(PlainButton)`
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid ${(p) => sx(p.theme).line2};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => sx(p.theme).text2};
  transition: color 120ms ease, border-color 120ms ease;

  &:hover {
    color: ${(p) => sx(p.theme).text};
    border-color: ${ORANGE};
  }
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${(p) => (p.$mobile ? '16px 16px 32px' : '22px 28px 32px')};
`;

const Title = styled.h2`
  margin: 0;
  font-size: ${(p) => (p.$mobile ? '22px' : '26px')};
  line-height: 1.15;
  font-weight: 800;
  letter-spacing: ${(p) => (p.$mobile ? '-0.3px' : '-0.4px')};
  overflow-wrap: anywhere;
  color: ${(p) => sx(p.theme).text};
`;

const TitleMuted = styled.span`
  font-weight: 700;
  color: ${(p) => sx(p.theme).label};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${(p) => (p.$mobile ? '8px' : '10px')};
  margin-top: ${(p) => (p.$mobile ? '12px' : '14px')};

  &:empty {
    display: none;
  }
`;

const Chip = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: ${(p) => (p.$mobile ? '40px' : '38px')};
  max-width: 100%;
  box-sizing: border-box;
  padding: 5px 12px 5px 10px;
  border-radius: 11px;
  border: 1px solid ${(p) => sx(p.theme).line2};
  background: ${(p) => sx(p.theme).inset};
  color: ${(p) => sx(p.theme).text};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  text-align: left;
  ${(p) => (p.$mobile ? 'flex: 1 1 100%;' : '')}
  cursor: ${(p) => (p.$static ? 'default' : 'pointer')};

  &:hover {
    border-color: ${(p) => (p.$static ? sx(p.theme).line2 : sx(p.theme).tintB)};
  }
`;

const ChipIcon = styled.span`
  flex: none;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => sx(p.theme).tint};
  color: ${ORANGE};
`;

const ChipValue = styled.span`
  min-width: 0;
  white-space: ${(p) => (p.$mobile ? 'normal' : 'nowrap')};
  overflow-wrap: anywhere;
  font-variant-numeric: tabular-nums;
`;

const ChipCopy = styled.span`
  flex: none;
  display: flex;
  margin-left: 2px;
  color: ${(p) => sx(p.theme).label};
`;

const Note = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 14px;
  padding: 10px 14px;
  border-radius: 10px;
  background: ${(p) => sx(p.theme).tint};
  border: 1px solid ${(p) => sx(p.theme).tintB};
  color: ${(p) => sx(p.theme).text2};
  font-size: 13px;
  line-height: 1.45;

  svg {
    color: ${ORANGE};
  }

  strong {
    font-weight: 700;
    color: ${(p) => sx(p.theme).text};
  }
`;

const Section = styled.section`
  margin-top: ${(p) => (p.$mobile ? '20px' : '24px')};
`;

const SectionLabel = styled.h3`
  margin: 0 0 10px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.8px;
  text-transform: uppercase;
  color: ${(p) => sx(p.theme).otext};
`;

const Grid = styled.div`
  display: grid;
  gap: 1px;
  background: ${(p) => sx(p.theme).line};
  border: 1px solid ${(p) => sx(p.theme).line};
  border-radius: 12px;
  overflow: hidden;
`;

const Cell = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: ${(p) => (p.$mobile ? '12px' : '10px')};
  min-width: 0;
  padding: ${(p) => (p.$editing ? '12px 16px' : '14px 16px')};
  background-color: ${(p) => sx(p.theme).card};
  background-image: ${(p) =>
    p.$dirty
      ? 'linear-gradient(rgba(250, 70, 22, 0.06), rgba(250, 70, 22, 0.06))'
      : 'none'};
  outline: none;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  transition: background-color 120ms ease;

  &:hover,
  &:focus-visible {
    background-image: ${(p) =>
      p.$clickable
        ? `linear-gradient(${sx(p.theme).hover}, ${sx(p.theme).hover})`
        : undefined};
  }

  &:focus-visible {
    box-shadow: ${(p) =>
      p.$clickable ? 'inset 0 0 0 2px rgba(250, 70, 22, 0.55)' : 'none'};
  }
`;

const IconTile = styled.div`
  flex: none;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  box-sizing: border-box;
  border-radius: 5px;
  background: ${(p) => sx(p.theme).tint};
  border: 1px solid ${(p) => sx(p.theme).tintB};
  color: ${ORANGE};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CellMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const Key = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: ${(p) => (p.$editing ? '0 0 6px 0' : '0 26px 3px 0')};
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  line-height: 1.35;
  color: ${(p) => sx(p.theme).label};
`;

const Required = styled.span`
  color: ${(p) => sx(p.theme).otext};
`;

const DirtyDot = styled.span`
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${ORANGE};
  box-shadow: 0 0 0 3px rgba(250, 70, 22, 0.2);
`;

const Value = styled.div`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  overflow-wrap: anywhere;
  font-variant-numeric: tabular-nums;
  color: ${(p) => sx(p.theme).text};
`;

const Pen = styled.span`
  position: absolute;
  top: ${(p) => (p.$mobile ? '8px' : '10px')};
  right: ${(p) => (p.$mobile ? '8px' : '10px')};
  width: ${(p) => (p.$mobile ? '24px' : '26px')};
  height: ${(p) => (p.$mobile ? '24px' : '26px')};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => sx(p.theme).otext};
  background: ${(p) => (p.$mobile ? 'transparent' : sx(p.theme).tint)};
  opacity: ${(p) => (p.$mobile ? 1 : 0)};
  transform: ${(p) => (p.$mobile ? 'none' : 'scale(0.9)')};
  transition: opacity 120ms ease, transform 120ms ease;
  pointer-events: none;

  ${Cell}:hover &,
  ${Cell}:focus-visible & {
    opacity: 1;
    transform: none;
  }
`;

const FieldError = styled.div`
  margin-top: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: ${(p) => sx(p.theme).error};
`;

const ConfirmText = styled.span`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  color: ${(p) => sx(p.theme).text2};
`;

const isTyping = (el) =>
  !!el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName || '');

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// Card width → grid columns, following resizes.
const useColumns = (ref) => {
  const [n, setN] = useState(3);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const calc = () => setN(columnsForWidth(el.clientWidth));
    calc();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(calc);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return n;
};

const copyText = (text) =>
  Promise.resolve().then(() => {
    const clipboard =
      typeof navigator === 'undefined' ? null : navigator.clipboard;
    if (!clipboard || typeof clipboard.writeText !== 'function') {
      throw new Error('Clipboard unavailable');
    }
    return clipboard.writeText(text);
  });

// The signed URL of an entry's answer file for `fieldId`, when the server
// sent one (a drawn signature's PNG).
export const answerFileUrl = (entry, fieldId) => {
  const files = (entry && entry.files) || [];
  const hit = files.find(
    (f) => f && f.field_id === fieldId && f.purpose === 'answer' && f.url
  );
  return hit ? hit.url : null;
};

const EntryCard = ({
  entry,
  definition,
  nav,
  isMobile,
  onClose,
  onSave,
  onPdf,
  onExcel,
  onDownloadFile,
  downloadingId,
  showToast,
  registerEscape,
  onEditingChange,
  extraActions,
  extraSections,
  editableFieldIds,
  downloadItems,
  onReveal,
  revealed,
  revealingId,
}) => {
  const answers = useMemo(() => entry.answers || {}, [entry.answers]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(answers);
  const [focusKey, setFocusKey] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [confirmClose, setConfirmClose] = useState(false);
  const innerRef = useRef(null);
  const bodyRef = useRef(null);
  const n = useColumns(innerRef);

  const sections = useMemo(() => sectionsOf(definition), [definition]);
  const allFields = useMemo(
    () => sections.reduce((acc, s) => acc.concat(s.fields), []),
    [sections]
  );
  const isEditable = useCallback(
    (field) =>
      !editableFieldIds ||
      (Array.isArray(editableFieldIds) && editableFieldIds.includes(field.id)),
    [editableFieldIds]
  );
  // Only editable fields take part in the diff and the patch.
  const fields = useMemo(
    () => allFields.filter(isEditable),
    [allFields, isEditable]
  );
  const canEdit = fields.length > 0;
  const contact = useMemo(() => contactOf(definition), [definition]);
  const values = editing ? draft : answers;
  const changed = editing ? changedKeys(draft, answers, fields) : [];
  const hero = heroOf(definition, values, entry);

  // A new entry (Prev / Next) starts clean at the top.
  useEffect(() => {
    setEditing(false);
    setMenuOpen(false);
    setConfirmClose(false);
    setErrors({});
    setDraft(entry.answers || {});
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [entry.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onEditingChange) onEditingChange(editing);
  }, [editing, onEditingChange]);

  const begin = (key) => {
    setDraft(answers);
    setErrors({});
    setEditing(true);
    setFocusKey(key || null);
    setMenuOpen(false);
  };
  const cancel = useCallback(() => {
    setEditing(false);
    setErrors({});
    setConfirmClose(false);
    setDraft(answers);
  }, [answers]);

  const save = async () => {
    if (saving) return;
    if (!changed.length) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const { error } = await onSave(patchOf(draft, changed, fields));
    setSaving(false);
    if (error) {
      setErrors(error.fields || {});
      showToast({ type: 'error', title: error.global });
      return;
    }
    setEditing(false);
    setErrors({});
    showToast({
      type: 'success',
      title: `${entry.reference} updated · ${plural(changed.length, 'field')}`,
    });
  };

  // Focus the control of the tile that was clicked.
  useEffect(() => {
    if (!editing || !focusKey || !innerRef.current) return;
    const cell = innerRef.current.querySelector(`[data-k="${focusKey}"]`);
    const el = cell && cell.querySelector('input, textarea, button');
    if (el) {
      el.focus();
      if (el.tagName === 'INPUT' && el.select) el.select();
    }
    setFocusKey(null);
  }, [editing, focusKey]);

  // Esc, as asked by the modal: close the menu, leave a field, drop an
  // edit with no changes; only then may the card close. → true when used.
  const onEscape = () => {
    if (menuOpen) {
      setMenuOpen(false);
      return true;
    }
    if (confirmClose) {
      setConfirmClose(false);
      return true;
    }
    if (!editing) return false;
    const active =
      typeof document === 'undefined' ? null : document.activeElement;
    if (isTyping(active)) {
      active.blur();
      return true;
    }
    if (!changed.length) cancel();
    return true;
  };
  const escapeRef = useRef(onEscape);
  escapeRef.current = onEscape;
  useEffect(() => {
    if (registerEscape) registerEscape(() => escapeRef.current());
    return () => {
      if (registerEscape) registerEscape(null);
    };
  }, [registerEscape]);

  // ⌘/Ctrl + Enter saves; ← → step through entries when not editing.
  const keyRef = useRef(null);
  keyRef.current = (e) => {
    if (editing && e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      save();
      return;
    }
    if (editing || !nav || isTyping(e.target) || menuOpen) return;
    if (e.key === 'ArrowLeft' && nav.index > 0) nav.onPrev();
    else if (e.key === 'ArrowRight' && nav.index < nav.total - 1) nav.onNext();
  };
  useEffect(() => {
    const onKey = (e) => keyRef.current(e);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const requestClose = () => {
    if (editing && changed.length) {
      setConfirmClose(true);
      return;
    }
    onClose();
  };

  const copy = (text, label) =>
    copyText(text).then(
      () => showToast({ type: 'success', title: `${label} copied` }),
      () =>
        showToast({
          type: 'error',
          title: `Couldn't copy the ${label.toLowerCase()}`,
        })
    );

  const chip = (field, icon, copyable) => {
    if (!field) return null;
    const text = formatAnswer(field, values[field.id]);
    if (!text) return null;
    const label = field.label || 'Answer';
    return (
      <Chip
        key={field.id}
        $mobile={isMobile}
        $static={!copyable}
        as={copyable ? undefined : 'div'}
        title={undefined}
        data-tip={copyable ? `${label} · click to copy` : label}
        aria-label={copyable ? `Copy ${label}: ${text}` : undefined}
        onClick={copyable ? () => copy(text, label) : undefined}
      >
        <ChipIcon>
          <Glyph name={icon} size={11} />
        </ChipIcon>
        <ChipValue $mobile={isMobile}>{text}</ChipValue>
        {copyable ? (
          <ChipCopy>
            <Glyph name="copy" size={13} />
          </ChipCopy>
        ) : null}
      </Chip>
    );
  };

  let actions;
  if (confirmClose) {
    actions = (
      <>
        <ConfirmText>{`Discard ${plural(
          changed.length,
          'change'
        )}?`}</ConfirmText>
        <TextButton $mobile={isMobile} onClick={() => setConfirmClose(false)}>
          Keep editing
        </TextButton>
        <TextButton $mobile={isMobile} $primary onClick={onClose}>
          Discard
        </TextButton>
      </>
    );
  } else if (editing) {
    actions = (
      <>
        {changed.length && !isMobile ? (
          <Dirty>{`${plural(changed.length, 'unsaved change')}`}</Dirty>
        ) : null}
        <TextButton $mobile={isMobile} onClick={cancel} disabled={saving}>
          Cancel
        </TextButton>
        <TextButton
          $mobile={isMobile}
          $primary
          onClick={save}
          disabled={!changed.length || saving}
        >
          <Glyph name="check" size={16} />
          {saving ? 'Saving…' : 'Save changes'}
        </TextButton>
      </>
    );
  } else {
    actions = (
      <>
        {nav ? (
          <Nav $mobile={isMobile} data-tour="entry-nav">
            <NavButton
              $mobile={isMobile}
              onClick={nav.onPrev}
              disabled={nav.index <= 0}
              aria-label="Previous entry"
              data-help="entry-prev"
            >
              <Glyph name="chevron-left" size={17} />
            </NavButton>
            <NavText>{`${(
              nav.index + 1
            ).toLocaleString()} of ${nav.total.toLocaleString()}`}</NavText>
            <NavButton
              $mobile={isMobile}
              onClick={nav.onNext}
              disabled={nav.index >= nav.total - 1}
              aria-label="Next entry"
              data-help="entry-next"
            >
              <Glyph name="chevron-right" size={17} />
            </NavButton>
          </Nav>
        ) : null}
        {extraActions}
        <DownloadMenu
          reference={entry.reference}
          open={menuOpen}
          onOpenChange={setMenuOpen}
          onPdf={onPdf}
          onExcel={onExcel}
          compact={isMobile}
          extraItems={downloadItems}
        />
        {canEdit ? (
          <EditButton onClick={() => begin()} data-tour="entry-edit">
            <Glyph name="pencil" size={14} />
            Edit
          </EditButton>
        ) : null}
      </>
    );
  }

  return (
    <Shell data-tour="entry-card">
      <Bar $mobile={isMobile}>
        <Meta>
          <MetaItem>
            <MetaKey>Entry</MetaKey>
            <MetaBox $mobile={isMobile}>{entry.reference}</MetaBox>
          </MetaItem>
          <MetaItem>
            <MetaKey>Submitted</MetaKey>
            <MetaBox $mobile={isMobile}>
              {formatDateTime(entry.submitted_at)}
            </MetaBox>
          </MetaItem>
          {entry.edited_at ? (
            <Edited
              data-tip-title="Edited"
              data-tip={
                entry.edited_by_name
                  ? `Changed by ${entry.edited_by_name} on ${formatDateTime(
                      entry.edited_at
                    )}`
                  : 'Changed by your team after it was submitted'
              }
            >
              <Glyph name="pencil" size={12} />
              Edited
            </Edited>
          ) : null}
        </Meta>
        <Actions $mobile={isMobile}>
          {actions}
          <CloseButton
            onClick={requestClose}
            aria-label="Close"
            data-help="entry-close"
          >
            <Glyph name="x" size={18} />
          </CloseButton>
        </Actions>
      </Bar>
      <Body ref={bodyRef} $mobile={isMobile}>
        <div ref={innerRef}>
          <div data-tour="entry-hero">
            <Title $mobile={isMobile}>
              {hero.title}
              {hero.suffix ? (
                <TitleMuted>{` ${hero.suffix}`}</TitleMuted>
              ) : null}
            </Title>
            {editing ? (
              <Note>
                <Glyph name="info" size={16} />
                <span>
                  {'You’re editing '}
                  <strong>{entry.reference}</strong>
                  {
                    '. Saved changes show in the table and in exports. ⌘/Ctrl + Enter saves.'
                  }
                </span>
              </Note>
            ) : (
              <Chips $mobile={isMobile}>
                {chip(contact.person, 'user', false)}
                {chip(contact.phone, 'phone', true)}
                {chip(contact.email, 'mail', true)}
              </Chips>
            )}
          </div>
          {sections.length ? null : (
            <Section $mobile={isMobile}>
              <Value style={{ color: 'inherit', fontWeight: 500 }}>
                This entry has no answers
              </Value>
            </Section>
          )}
          {sections.map((section, sectionIndex) => {
            const sp = spans(section.fields, n);
            return (
              <Section key={section.id} $mobile={isMobile}>
                <SectionLabel>{section.label}</SectionLabel>
                <Grid
                  data-tour={sectionIndex === 0 ? 'entry-answers' : undefined}
                  style={{
                    gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`,
                  }}
                >
                  {section.fields.map((field, i) => {
                    const editable = isEditable(field);
                    const dirty = changed.includes(field.id);
                    const error = editing ? errors[field.id] : null;
                    const label = field.label || 'Untitled field';
                    const clickable = !editing && editable;
                    const props = {
                      key: field.id,
                      'data-k': field.id,
                      'data-fb-readonly': editable ? undefined : '',
                      $mobile: isMobile,
                      $editing: editing,
                      $dirty: dirty,
                      $clickable: clickable,
                      style:
                        sp[i] > 1 ? { gridColumn: `span ${sp[i]}` } : undefined,
                    };
                    if (clickable) {
                      Object.assign(props, {
                        role: 'button',
                        tabIndex: 0,
                        'aria-label': `Edit ${label}`,
                        onClick: () => begin(field.id),
                        onKeyDown: (e) => {
                          if (e.target !== e.currentTarget) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            begin(field.id);
                          }
                        },
                      });
                    }
                    return (
                      <Cell {...props}>
                        <IconTile aria-hidden="true">
                          <Glyph name={iconFor(field)} size={11} />
                        </IconTile>
                        <CellMain>
                          <Key $editing={editing}>
                            <span>{label}</span>
                            {editing && editable && field.required ? (
                              <Required>*</Required>
                            ) : null}
                            {dirty ? <DirtyDot title="Changed" /> : null}
                          </Key>
                          {editing && editable ? (
                            <>
                              <EntryEditor
                                field={field}
                                value={draft[field.id]}
                                invalid={!!error}
                                inputId={`fb-entry-${field.id}`}
                                onChange={(v) =>
                                  setDraft((d) => ({ ...d, [field.id]: v }))
                                }
                              />
                              {error ? (
                                <FieldError role="alert">{error}</FieldError>
                              ) : null}
                            </>
                          ) : (
                            <Value>
                              <EntryValue
                                field={field}
                                value={values[field.id]}
                                onDownload={onDownloadFile}
                                downloading={
                                  !!values[field.id] &&
                                  downloadingId != null &&
                                  values[field.id].file_id === downloadingId
                                }
                                onReveal={onReveal}
                                revealed={
                                  (revealed && revealed[field.id]) || null
                                }
                                revealing={revealingId === field.id}
                                imageUrl={
                                  field.type === 'signature'
                                    ? answerFileUrl(entry, field.id)
                                    : null
                                }
                              />
                            </Value>
                          )}
                        </CellMain>
                        {editing || !editable ? null : (
                          <Pen $mobile={isMobile} aria-hidden="true">
                            <Glyph name="pencil" size={13} />
                          </Pen>
                        )}
                      </Cell>
                    );
                  })}
                </Grid>
              </Section>
            );
          })}
          {extraSections}
        </div>
      </Body>
    </Shell>
  );
};

EntryCard.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.number.isRequired,
    reference: PropTypes.string.isRequired,
    submitted_at: PropTypes.string,
    edited_at: PropTypes.string,
    edited_by_name: PropTypes.string,
    submitter_name: PropTypes.string,
    answers: PropTypes.object,
    files: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  definition: PropTypes.object,
  nav: PropTypes.shape({
    index: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    onPrev: PropTypes.func.isRequired,
    onNext: PropTypes.func.isRequired,
  }),
  isMobile: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onPdf: PropTypes.func.isRequired,
  onExcel: PropTypes.func.isRequired,
  onDownloadFile: PropTypes.func.isRequired,
  downloadingId: PropTypes.number,
  showToast: PropTypes.func.isRequired,
  registerEscape: PropTypes.func,
  onEditingChange: PropTypes.func,
  // W-9 slots (w9-plan T15); all optional.
  extraActions: PropTypes.node,
  extraSections: PropTypes.node,
  editableFieldIds: PropTypes.arrayOf(PropTypes.string),
  downloadItems: PropTypes.arrayOf(PropTypes.object),
  onReveal: PropTypes.func,
  revealed: PropTypes.objectOf(PropTypes.string),
  revealingId: PropTypes.string,
};

EntryCard.defaultProps = {
  definition: null,
  nav: null,
  isMobile: false,
  downloadingId: null,
  registerEscape: undefined,
  onEditingChange: undefined,
  extraActions: null,
  extraSections: null,
  editableFieldIds: null,
  downloadItems: undefined,
  onReveal: undefined,
  revealed: null,
  revealingId: null,
};

export { BarButton };
export default EntryCard;
